import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  LinearProgress,
} from "@mui/material";
import {
  ShareOutlined as ShareIcon,
  EmojiEvents as TrophyIcon,
  Casino as DicesIcon,
  Shuffle as ShuffleIcon,
  RestartAlt as ClearIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  CheckCircle as VerifiedIcon,
  Bolt as InstantIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import web3 from "../../../../Service/web3";
import cinstance from "../../../../Service/randomcinstance";
import { colors, borderRadiusPx } from "../../../../../theme";
import { Path, DEFAULT_CURRENCY } from "../../../../../static/Constants";
import getImageUrlByHash from "../../../../../static/logoUtils";
import {
  CommonButton,
  CommonChip,
  FailedPopup,
} from "../../../../Components/ui";
import ModernSpinWheel from "./ModernSpinWheel";

const getPastEventsSafe = async (eventName, options = {}) => {
  if (cinstance) {
    try {
      const evts = await cinstance.getPastEvents(eventName, options);
      if (evts && evts.length >= 0) return evts;
    } catch (err) {
      console.warn(`[SpinWheel] getPastEvents error:`, err?.message || err);
    }
  }
  return [];
};

let pastEventsPromise = null;
let lastQueryTimestamp = 0;

const getPastEventsBatch = async (fromBlock) => {
  const now = Date.now();
  if (pastEventsPromise && now - lastQueryTimestamp < 3000) {
    return pastEventsPromise;
  }
  lastQueryTimestamp = now;
  pastEventsPromise = getPastEventsSafe("SpinLotteryResult", {
    fromBlock: fromBlock,
    toBlock: "latest",
  }).finally(() => {
    setTimeout(() => {
      if (Date.now() - lastQueryTimestamp >= 3000) {
        pastEventsPromise = null;
      }
    }, 3000);
  });
  return pastEventsPromise;
};

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch (e) {
      return "0";
    }
  }
};

const SpinWheelCard = ({
  lotteryData,
  partner,
  partnerLogo,
  account = "",
  currency = DEFAULT_CURRENCY,
  onConnectWallet,
}) => {
  const [selectedNo, setSelectedNo] = useState("");
  const [mustSpin, setMustSpin] = useState(false);
  const [isSpinningContinuous, setIsSpinningContinuous] = useState(false);
  const [isSpinningTx, setIsSpinningTx] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [showFailed, setShowFailed] = useState(false);

  // Result dialog & outcome states
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [isWinner, setIsWinner] = useState(false);
  const [winningNumber, setWinningNumber] = useState(null);
  const [lastPlayedNumber, setLastPlayedNumber] = useState(null);
  const [hasPlayedThisSession, setHasPlayedThisSession] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const capacity = Math.max(2, Number(lotteryData?.capacity) || 10);
  const entryFeeFormatted = useMemo(
    () => safeFromWei(lotteryData?.entryFee),
    [lotteryData?.entryFee],
  );
  const totalPrizeFormatted = useMemo(
    () => safeFromWei(lotteryData?.totalPrize),
    [lotteryData?.totalPrize],
  );
  const partnerName =
    partner?.name || partner?.companyName || "AutoBET Official";
  const partnerLogoUrl = getImageUrlByHash(partner?.logoHash, partnerLogo);

  const isRollover = lotteryData?.status === "5";

  // Check if current user is the on-chain winner of this wheel
  const isUserOnChainWinner = useMemo(() => {
    const winnerAddr = (lotteryData?.lotteryWinner || "").toLowerCase();
    const myAddr = (account || "").toLowerCase();
    return (
      winnerAddr &&
      winnerAddr !== "0x0000000000000000000000000000000000000000" &&
      winnerAddr !== "0x0" &&
      winnerAddr === myAddr
    );
  }, [lotteryData?.lotteryWinner, account]);

  // Load past user spin history for this wheel on mount
  useEffect(() => {
    let isMounted = true;
    const fetchPastPlays = async () => {
      if (!account || !lotteryData?.lotteryId) return;
      try {
        const latestBlock = await web3.eth.getBlockNumber().catch(() => null);
        const fromBlock = latestBlock ? Math.max(0, latestBlock - 2000) : 0;
        const events = await getPastEventsBatch(fromBlock);

        const myEvents = (events || []).filter(
          (e) =>
            String(e?.returnValues?.lotteryId) ===
              String(lotteryData.lotteryId) &&
            ((e?.returnValues?.useraddressdata || "").toLowerCase() ===
              account.toLowerCase() ||
              (e?.returnValues?.useraddress || "").toLowerCase() ===
                account.toLowerCase() ||
              (e?.returnValues?.buyer || "").toLowerCase() ===
                account.toLowerCase()),
        );

        if (myEvents.length > 0 && isMounted) {
          const lastEvent = myEvents[myEvents.length - 1];
          const winNum = Number(lastEvent.returnValues.winnerNum || 1);
          const selNum = Number(lastEvent.returnValues.selectedNum || 1);
          const won = winNum === selNum;

          console.log(
            `[SpinWheel #${lotteryData.lotteryId}] Loaded previous spin record:`,
            {
              winnerNum: winNum,
              selectedNum: selNum,
              isWinner: won,
              block: lastEvent.blockNumber,
            },
          );

          setWinningNumber(winNum);
          setLastPlayedNumber(selNum);
          setIsWinner(won);
          setHasPlayedThisSession(true);
        }
      } catch (err) {
        console.warn(
          `[SpinWheel #${lotteryData.lotteryId}] Could not fetch past spin events:`,
          err,
        );
      }
    };

    fetchPastPlays();
    return () => {
      isMounted = false;
    };
  }, [account, lotteryData?.lotteryId]);

  // Handle Quick Pick (Random)
  const handleRandomPick = () => {
    const randomNum = Math.floor(Math.random() * capacity) + 1;
    console.log(
      `[SpinWheel #${lotteryData.lotteryId}] Lucky Pick action: Selected #${randomNum} (Range 1-${capacity})`,
    );
    setSelectedNo(`${randomNum}`);
  };

  // Handle Share link
  const handleShare = () => {
    const playPath = Path.Spinnerlottery || "/spinnerlottery";
    const url = `${window.location.origin}${playPath}`;
    console.log(
      `[SpinWheel #${lotteryData.lotteryId}] Share link action copied to clipboard:`,
      url,
    );
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success(
        `Shareable link copied for SpinBET #${lotteryData.lotteryId}!`,
      );
    }
  };

  // Execute Spin Bet On-Chain
  const handleSpinClick = async () => {
    if (!selectedNo) {
      console.warn(
        `[SpinWheel #${lotteryData.lotteryId}] Spin aborted: No number selected.`,
      );
      toast.warn("Please select a lucky number before spinning!");
      return;
    }

    if (!account) {
      console.warn(
        `[SpinWheel #${lotteryData.lotteryId}] Spin aborted: Wallet not connected.`,
      );
      if (onConnectWallet) onConnectWallet();
      return;
    }

    console.log(
      `[SpinWheel #${lotteryData.lotteryId}] 'SPIN & BUY' action initiated:`,
      {
        selectedNumber: selectedNo,
        entryFeeWei: lotteryData.entryFee,
        entryFeeEth: entryFeeFormatted,
        currency,
        userAccount: account,
      },
    );

    try {
      setIsSpinningTx(true);
      setStatusMessage("Confirming bet transaction on wallet...");

      console.log(
        `[SpinWheel #${lotteryData.lotteryId}] Submitting transaction to buySpinnerLottery...`,
      );

      let spinStarted = false;
      const startSpinOnApproval = () => {
        if (!spinStarted) {
          spinStarted = true;
          setIsSpinningContinuous(true);
          setStatusMessage(
            "Transaction approved! Wheel spinning while generating on-chain VRF draw...",
          );
        }
      };

      const sendPromise = cinstance.methods
        .buySpinnerLottery(selectedNo, lotteryData.lotteryId)
        .send({
          from: account,
          value: lotteryData.entryFee,
          gasLimit: 1000000,
        })
        .on("transactionHash", (hash) => {
          console.log(
            `[SpinWheel #${lotteryData.lotteryId}] Transaction approved, hash:`,
            hash,
          );
          startSpinOnApproval();
        });

      const sendTx = await sendPromise;
      startSpinOnApproval();

      const txBlock = Number(sendTx.blockNumber) || 0;
      console.log(
        `[SpinWheel #${lotteryData.lotteryId}] Bet transaction confirmed on-chain!`,
        {
          txHash: sendTx.transactionHash,
          blockNumber: txBlock,
          gasUsed: sendTx.gasUsed,
          events: Object.keys(sendTx.events || {}),
        },
      );

      setStatusMessage("Bet confirmed! Generating VRF random draw...");

      let resultNumber = null;
      let won = false;

      // 1. Check if event is returned directly in sendTx.events
      if (sendTx.events && sendTx.events.SpinLotteryResult) {
        const ret = sendTx.events.SpinLotteryResult.returnValues;
        resultNumber = Number(ret.winnerNum || ret.number || selectedNo);
        won = Number(resultNumber) === Number(selectedNo);
        console.log(
          `[SpinWheel #${lotteryData.lotteryId}] SpinLotteryResult found directly in tx receipt:`,
          ret,
        );
      } else if (sendTx.events && sendTx.events.WinnerPaid) {
        resultNumber = Number(selectedNo);
        won = true;
        console.log(
          `[SpinWheel #${lotteryData.lotteryId}] WinnerPaid found directly in tx receipt:`,
          sendTx.events.WinnerPaid.returnValues,
        );
      }

      // 2. Continuous listening for Chainlink VRF callback event (fulfillRandomness emits SpinLotteryResult)
      if (resultNumber === null) {
        const startBlock = Math.max(0, txBlock - 2);
        console.log(
          `[SpinWheel #${lotteryData.lotteryId}] Listening for Chainlink VRF draw event from block ${startBlock}...`,
        );

        let elapsedSeconds = 0;
        while (resultNumber === null && isMountedRef.current) {
          elapsedSeconds += 2;
          setStatusMessage(
            `The result is currently being generated. Wheel spinning while awaiting on-chain draw (${elapsedSeconds}s)...`,
          );

          try {
            const pastEvts = await getPastEventsSafe("SpinLotteryResult", {
              fromBlock: startBlock,
              toBlock: "latest",
            });

            const matchEvt = pastEvts
              .reverse()
              .find(
                (e) =>
                  String(e?.returnValues?.lotteryId) ===
                    String(lotteryData.lotteryId) &&
                  ((e?.returnValues?.useraddressdata || "").toLowerCase() ===
                    account.toLowerCase() ||
                    (e?.returnValues?.useraddress || "").toLowerCase() ===
                      account.toLowerCase() ||
                    (e?.returnValues?.buyer || "").toLowerCase() ===
                      account.toLowerCase()) &&
                  Number(e.blockNumber) >= startBlock,
              );

            if (matchEvt) {
              resultNumber = Number(
                matchEvt.returnValues.winnerNum || matchEvt.returnValues.number,
              );
              const selNum = Number(
                matchEvt.returnValues.selectedNum || selectedNo,
              );
              won = Number(resultNumber) === Number(selNum);
              console.log(
                `[SpinWheel #${lotteryData.lotteryId}] Chainlink VRF SpinLotteryResult event received:`,
                {
                  event: matchEvt.returnValues,
                  blockNumber: matchEvt.blockNumber,
                  winnerNumber: resultNumber,
                  selectedNumber: selNum,
                  isWinner: won,
                },
              );
              break;
            }
          } catch (evtErr) {
            console.warn(
              `[SpinWheel #${lotteryData.lotteryId}] Event polling attempt error:`,
              evtErr,
            );
          }

          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      // If draw response received from VRF:
      if (resultNumber !== null && isMountedRef.current) {
        console.log(
          `[SpinWheel #${lotteryData.lotteryId}] Setting winning draw outcome: #${resultNumber} (User Pick: #${selectedNo}, Won: ${won})`,
        );
        setWinningNumber(resultNumber);
        setLastPlayedNumber(Number(selectedNo));
        setIsWinner(won);
        setHasPlayedThisSession(true);

        // Smoothly transition from continuous spinning to landing on result slice
        console.log(
          `[SpinWheel #${lotteryData.lotteryId}] Transitioning wheel spin animation to slice #${resultNumber}...`,
        );
        setStatusMessage(`Draw resolved! Landing on slice #${resultNumber}...`);
        setIsSpinningContinuous(false);
        setMustSpin(true);
      }
    } catch (err) {
      console.error(
        `[SpinWheel #${lotteryData.lotteryId}] Spin transaction failed:`,
        err,
      );
      setIsSpinningContinuous(false);
      setIsSpinningTx(false);
      setMustSpin(false);
      setStatusMessage("");
      setShowFailed(true);
    }
  };

  const handleStopSpinning = () => {
    console.log(
      `[SpinWheel #${
        lotteryData.lotteryId
      }] Spin animation ended on target #${winningNumber}. Outcome: ${
        isWinner ? "JACKPOT WINNER" : "MISSED"
      }`,
    );
    setMustSpin(false);
    setIsSpinningContinuous(false);
    setIsSpinningTx(false);
    setStatusMessage("");
    if (winningNumber !== null) {
      setResultModalOpen(true);
      if (isWinner) {
        console.log(
          `[SpinWheel #${lotteryData.lotteryId}] Displaying winner jackpot modal (+${totalPrizeFormatted} ${currency})`,
        );
        toast.success(
          `Congratulations! You're the lucky winner of the lottery! (+${totalPrizeFormatted} ${currency})`,
        );
      } else {
        console.log(
          `[SpinWheel #${
            lotteryData.lotteryId
          }] Displaying result modal (Landed #${winningNumber}, User #${
            lastPlayedNumber || selectedNo
          })`,
        );
        // toast.info(`Better luck next time. Winning Number is #${winningNumber}`);
        console.log(
          `Better luck next time. Winning Number is #${winningNumber}`,
        );
      }
    }
  };

  return (
    <Box
      className={`spin-wheel-card ${
        isSpinningTx || isSpinningContinuous || mustSpin ? "is-spinning" : ""
      }`}
    >
      {/* Card Header: Creator Info & Badges */}
      <Box sx={{ p: 2.5, pb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              src={partnerLogoUrl}
              alt={partnerName}
              sx={{
                width: 32,
                height: 32,
                bgcolor: colors.primary.lighter,
                color: colors.primary.main,
                fontWeight: 700,
              }}
            >
              {partnerName.charAt(0)}
            </Avatar>
            <div>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: colors.slate[900] }}
                >
                  {partnerName}
                </Typography>
                <VerifiedIcon
                  sx={{ fontSize: 14, color: colors.primary.main }}
                />
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: colors.slate[400], fontFamily: "monospace" }}
              >
                Wheel #{lotteryData.lotteryId}
              </Typography>
            </div>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.75}>
            {isUserOnChainWinner && (
              <CommonChip label="You Won!" variant="active" size="small" />
            )}
            {isRollover && !isUserOnChainWinner && (
              <CommonChip label="Rollover" variant="warning" size="small" />
            )}
            <CommonChip
              label={`${capacity} Slots`}
              variant="neutral"
              size="small"
            />
            <Tooltip title="Share Wheel Link" arrow>
              <IconButton
                size="small"
                onClick={handleShare}
                sx={{ color: colors.slate[400], p: 0.5 }}
              >
                <ShareIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Prize & Entry Fee Highlights */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: colors.slate[50],
          borderTop: `1px solid ${colors.slate[100]}`,
          borderBottom: `1px solid ${colors.slate[100]}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <div>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Prize Jackpot
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: colors.emerald[600],
                lineHeight: 1.2,
              }}
            >
              {totalPrizeFormatted}{" "}
              <span style={{ fontSize: "0.8rem" }}>{currency}</span>
            </Typography>
          </div>
          <div style={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Entry Ticket
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: colors.slate[800],
                lineHeight: 1.2,
              }}
            >
              {entryFeeFormatted}{" "}
              <span style={{ fontSize: "0.8rem" }}>{currency}</span>
            </Typography>
          </div>
        </Stack>
      </Box>

      {/* Interactive Canvas Wheel Stage */}
      <Box sx={{ px: 2, py: 1, position: "relative" }}>
        <ModernSpinWheel
          capacity={capacity}
          isSpinningContinuous={isSpinningContinuous}
          mustSpin={mustSpin}
          targetNumber={winningNumber || (selectedNo ? Number(selectedNo) : 1)}
          selectedNumber={selectedNo}
          winningNumber={winningNumber}
          isWinner={isWinner}
          onStopSpinning={handleStopSpinning}
          partner={partner}
          partnerLogo={partnerLogo}
        />

        {/* Live Processing Status Banner */}
        {isSpinningTx && (
          <Box className="spin-status-banner">
            <LinearProgress sx={{ width: "100%", borderRadius: "4px" }} />
            <Typography
              variant="caption"
              sx={{
                color: colors.primary.dark,
                fontWeight: 600,
                textAlign: "center",
                display: "block",
                mt: 0.5,
              }}
            >
              {statusMessage || "Spin in progress..."}
            </Typography>
          </Box>
        )}

        {/* Persistent Win / Result Indicator Banner */}
        {!isSpinningTx &&
          !mustSpin &&
          hasPlayedThisSession &&
          winningNumber !== null && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: borderRadiusPx.md,
                bgcolor: isWinner ? colors.emerald[50] : colors.slate[50],
                border: `1.5px solid ${
                  isWinner ? colors.emerald[400] : colors.slate[200]
                }`,
                boxShadow: isWinner
                  ? "0 4px 12px rgba(16, 185, 129, 0.15)"
                  : "none",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                {isWinner ? (
                  <TrophyIcon sx={{ fontSize: 28, color: colors.amber[500] }} />
                ) : (
                  <DicesIcon sx={{ fontSize: 24, color: colors.slate[500] }} />
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      color: isWinner ? colors.emerald[800] : colors.slate[800],
                      lineHeight: 1.2,
                    }}
                  >
                    {isWinner
                      ? `JACKPOT WINNER! (+${totalPrizeFormatted} ${currency})`
                      : `Wheel Result: Landed on #${winningNumber}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isWinner ? colors.emerald[700] : colors.slate[600],
                      display: "block",
                    }}
                  >
                    {isWinner
                      ? `Your pick #${
                          lastPlayedNumber || selectedNo
                        } hit the jackpot!`
                      : `Your pick: #${
                          lastPlayedNumber || selectedNo
                        }. Better luck next spin!`}
                  </Typography>
                </Box>
                <CommonChip
                  label={isWinner ? "WON" : "MISSED"}
                  variant={isWinner ? "active" : "neutral"}
                  size="small"
                  sx={{ fontWeight: 800 }}
                />
              </Stack>
            </Box>
          )}
      </Box>

      {/* Quick Segment Selector Grid */}
      <Box
        sx={{
          p: 2.5,
          pt: 1,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: colors.slate[700],
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Pick Number (1 - {capacity}):
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <CommonButton
              variant="outline"
              size="small"
              startIcon={<ShuffleIcon sx={{ fontSize: 13 }} />}
              onClick={handleRandomPick}
              disabled={isSpinningTx || mustSpin}
              sx={{ fontSize: "0.75rem", py: 0.2, px: 1 }}
            >
              Pick
            </CommonButton>
            {selectedNo && (
              <CommonButton
                variant="clearAction"
                size="small"
                startIcon={<ClearIcon sx={{ fontSize: 13 }} />}
                onClick={() => {
                  console.log(
                    `[SpinWheel #${lotteryData.lotteryId}] Selection cleared.`,
                  );
                  setSelectedNo("");
                }}
                disabled={isSpinningTx || mustSpin}
                sx={{
                  fontSize: "0.75rem",
                  py: 0.2,
                  px: 1,
                  color: colors.red[500],
                  borderColor: colors.red[200],
                  "&:hover": {
                    backgroundColor: colors.red[50],
                    borderColor: colors.red[400],
                    color: colors.red[500],
                  },
                }}
              >
                Clear
              </CommonButton>
            )}
          </Stack>
        </Stack>

        {/* Number Pills */}
        <div className="spin-numbers-grid">
          {Array.from({ length: capacity }, (_, idx) => {
            const numVal = `${idx + 1}`;
            const isSelected = selectedNo === numVal;
            const isWinningPill =
              winningNumber !== null &&
              Number(winningNumber) === Number(numVal) &&
              !mustSpin &&
              !isSpinningTx;
            const isJackpotHit = isWinningPill && isWinner;

            return (
              <div
                key={numVal}
                className={`spin-number-pill ${isSelected ? "selected" : ""} ${
                  isJackpotHit
                    ? "hit-jackpot"
                    : isWinningPill
                    ? "winner-pill"
                    : ""
                }`}
                onClick={() => {
                  if (!isSpinningTx && !mustSpin) {
                    const nextVal = isSelected ? "" : numVal;
                    console.log(
                      `[SpinWheel #${
                        lotteryData.lotteryId
                      }] Number pill clicked: #${numVal} -> ${
                        nextVal ? `Selected #${nextVal}` : "Deselected"
                      }`,
                    );
                    setSelectedNo(nextVal);
                  }
                }}
              >
                {numVal}
              </div>
            );
          })}
        </div>

        {/* Bet Summary & Spin Action Button */}
        <Box sx={{ mt: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              Selected:{" "}
              <strong
                style={{
                  color: selectedNo ? colors.primary.main : colors.slate[400],
                }}
              >
                {selectedNo ? `#${selectedNo}` : "None"}
              </strong>
            </Typography>
          </Stack>

          {!account ? (
            <CommonButton
              variant="primary"
              fullWidth
              startIcon={<WalletIcon />}
              onClick={onConnectWallet}
              sx={{ py: 1.25, fontWeight: 700 }}
            >
              Connect Wallet to Spin
            </CommonButton>
          ) : (
            <CommonButton
              variant="primary"
              fullWidth
              startIcon={<InstantIcon />}
              onClick={handleSpinClick}
              disabled={!selectedNo || isSpinningTx || mustSpin}
              loading={isSpinningTx}
              sx={{
                py: 1.25,
                fontWeight: 800,
                fontSize: "0.95rem",
                background: selectedNo
                  ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                  : undefined,
                boxShadow: selectedNo
                  ? "0 4px 14px rgba(59, 130, 246, 0.4)"
                  : undefined,
              }}
            >
              {isSpinningTx
                ? "Spinning..."
                : `SPIN & BUY (${entryFeeFormatted} ${currency})`}
            </CommonButton>
          )}
        </Box>
      </Box>

      {/* Result Outcome Modal */}
      <Dialog
        open={resultModalOpen}
        onClose={() => {
          console.log(
            `[SpinWheel #${lotteryData.lotteryId}] Result modal closed.`,
          );
          setResultModalOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: borderRadiusPx.surface,
            textAlign: "center",
            p: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          {isWinner ? (
            <Box
              className="winner-glow-box"
              sx={{
                p: 2,
                borderRadius: borderRadiusPx.lg,
                bgcolor: colors.amber[50],
              }}
            >
              <TrophyIcon
                sx={{ fontSize: 64, color: colors.amber[500], mb: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: colors.amber[800], mb: 1 }}
              >
                JACKPOT WINNER!
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.slate[700], mb: 2 }}
              >
                The wheel landed on your lucky number{" "}
                <strong>#{winningNumber}</strong>!
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "#ffffff",
                  borderRadius: borderRadiusPx.md,
                  border: `1px solid ${colors.amber[200]}`,
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: colors.slate[500], textTransform: "uppercase" }}
                >
                  Payout Transferred
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: colors.emerald[600] }}
                >
                  +{totalPrizeFormatted} {currency}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: 2,
                borderRadius: borderRadiusPx.lg,
                bgcolor: colors.slate[50],
              }}
            >
              <DicesIcon
                sx={{ fontSize: 56, color: colors.slate[400], mb: 1 }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: colors.slate[800], mb: 1 }}
              >
                Better Luck Next Spin!
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.slate[600], mb: 2 }}
              >
                The wheel landed on <strong>#{winningNumber}</strong> (You chose
                #{selectedNo}).
              </Typography>
            </Box>
          )}

          <CommonButton
            variant="primary"
            fullWidth
            onClick={() => {
              console.log(
                `[SpinWheel #${lotteryData.lotteryId}] 'Spin Again' clicked -> Resetting selection.`,
              );
              setResultModalOpen(false);
              setSelectedNo("");
            }}
            sx={{ mt: 3, py: 1.2 }}
          >
            Spin Again
          </CommonButton>
        </DialogContent>
      </Dialog>

      {/* Transaction Failure Modal */}
      <FailedPopup
        show={showFailed}
        setShowModal={() => {
          console.log(
            `[SpinWheel #${lotteryData.lotteryId}] Failed popup dismissed.`,
          );
          setShowFailed(false);
        }}
        lotteryType="revolver"
      />
    </Box>
  );
};

export default SpinWheelCard;
